import React from 'react';
import * as css from './AIMessageGenerator.css';
import GeneratorChatList from './generatorChatList/GeneratorChatList';
import GeneratorPromptArea from './generatorPromptArea/GeneratorPromptArea';
import GeneratorSuggestions from './generatorSuggestions/GeneratorSuggestions';
import {useScrollToLatestMessage} from './utils/useScrollToLatestMessage';
import CustomScrollbar from '../customScrollbar/CustomScrollbar';
import type {AIMessageGeneratorEmptyStateProps, AIMessageGeneratorPromptAreaProps, AIMessageGeneratorProps, AIMessageGeneratorUserMessageProps} from './utils/types';
import type {useAIMessageGenerator} from './utils/useAIMessageGenerator';
import type {useGeneratorPrompt} from './utils/useGeneratorPrompt';

interface AIMessageGeneratorPanelProps {
    generator: ReturnType<typeof useAIMessageGenerator>;
    prompt: ReturnType<typeof useGeneratorPrompt>;
    suggestions: string[];
    placeholder: string;
    submit: AIMessageGeneratorProps['submit'];
    approve?: AIMessageGeneratorProps['approve'];
    isMobile: boolean;
    hasPendingAttachment: boolean;
    onPendingAttachmentChange: (hasAttachment: boolean) => void;
    innerPop?: boolean;
    renderEmptyState?: (props: AIMessageGeneratorEmptyStateProps) => React.ReactNode;
    renderPromptArea?: (props: AIMessageGeneratorPromptAreaProps) => React.ReactNode;
    renderUserMessage?: (props: AIMessageGeneratorUserMessageProps) => React.ReactNode;
}

function AIMessageGeneratorPanel({
    generator,
    prompt,
    suggestions,
    placeholder,
    submit,
    approve,
    isMobile,
    hasPendingAttachment,
    onPendingAttachmentChange,
    innerPop = false,
    renderEmptyState,
    renderPromptArea,
    renderUserMessage,
}: AIMessageGeneratorPanelProps) {
    const {
        messages, promptText, draftLoading, approveLoading, error, resetKey,
        handleApprove, editUserMessage,
    } = generator;
    const {
        scrollColumnRef, promptTextareaRef,
        handleSuggestionClick, handlePromptChange, handlePromptKeyDown, handleSubmitPrompt,
    } = prompt;

    const PanelInner = innerPop ? css.innerPopPanelInner : css.panelInner;
    const disableSubmit = (!promptText.trim() && !hasPendingAttachment) || draftLoading;
    useScrollToLatestMessage(scrollColumnRef, messages.length, draftLoading);

    const promptAreaProps: AIMessageGeneratorPromptAreaProps = {
        promptText,
        placeholder,
        draftLoading,
        disableSubmit,
        promptTextareaRef,
        onPromptChange: handlePromptChange,
        onPromptKeyDown: handlePromptKeyDown,
        onSubmitPrompt: handleSubmitPrompt,
        onPendingAttachmentChange,
    };

    return (
        <PanelInner>
            <css.scrollArea>
                {!isMobile && (
                    <CustomScrollbar
                        contentRef={scrollColumnRef}
                        cssStyle="inset-inline-end: 3px;"
                        maxBottom={65}
                    />
                )}
                <css.scrollColumn ref={scrollColumnRef}>
                    {messages.length === 0 && (
                        renderEmptyState
                            ? renderEmptyState({onSuggestionClick: handleSuggestionClick})
                            : <GeneratorSuggestions suggestions={suggestions} onSuggestionClick={handleSuggestionClick} />
                    )}
                    {(messages.length > 0 || draftLoading) && (
                        <GeneratorChatList
                            messages={messages}
                            draftLoading={draftLoading}
                            approveLabel={approve?.label}
                            approveLoading={approveLoading}
                            onApprove={approve ? handleApprove : undefined}
                            onEditMessage={editUserMessage}
                            renderUserMessage={renderUserMessage}
                        />
                    )}
                </css.scrollColumn>
                {error && (
                    <css.errorText role="alert" data-testid="ai-message-generator-error">
                        {error}
                    </css.errorText>
                )}
            </css.scrollArea>
            {renderPromptArea ? (
                <React.Fragment key={resetKey}>{renderPromptArea(promptAreaProps)}</React.Fragment>
            ) : (
                <GeneratorPromptArea
                    promptText={promptText}
                    placeholder={placeholder}
                    disableSubmit={disableSubmit}
                    submitLabel={submit.label}
                    submitXhrState={submit.xhrState}
                    promptTextareaRef={promptTextareaRef}
                    onPromptChange={handlePromptChange}
                    onPromptKeyDown={handlePromptKeyDown}
                    onSubmitPrompt={() => handleSubmitPrompt()}
                />
            )}
        </PanelInner>
    );
}

export default AIMessageGeneratorPanel;
